/**
 * UI primitives — light-mode token wrappers.
 * API is UNCHANGED — all existing imports continue to work.
 * Visual classes have been updated to use design tokens from tokens.css.
 */
import type {
  ReactNode,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

const cls = (...parts: (string | false | undefined | null)[]): string =>
  parts.filter(Boolean).join(' ');

export const Card: React.FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cls(
      'rounded-card border border-border bg-surface p-3 shadow-card sm:p-4',
      className,
    )}
  >
    {children}
  </div>
);

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'primary' | 'ghost' | 'danger' | 'secondary';
    size?: 'sm' | 'md';
  }
>(({ variant = 'primary', size = 'md', className, ...props }, ref) => {
  const variants = {
    primary:
      'bg-brand text-white hover:bg-brand-dark active:bg-brand-dark shadow-sm disabled:bg-ink-muted/30 disabled:text-ink-muted',
    secondary:
      'bg-surface text-ink hover:bg-surface-raised border border-border disabled:text-ink-muted',
    ghost:
      'bg-transparent text-ink hover:bg-surface-raised border border-border disabled:text-ink-muted',
    danger:
      'bg-neg text-white hover:bg-red-700 active:bg-red-800 shadow-sm disabled:bg-ink-muted/30 disabled:text-ink-muted',
  };
  const sizes = {
    sm: 'min-h-[44px] px-3 py-1.5 text-small rounded-md gap-1.5',
    md: 'min-h-[44px] px-4 py-2.5 text-body rounded-card gap-2',
  };
  return (
    <button
      ref={ref}
      className={cls(
        'inline-flex items-center justify-center font-medium transition-all disabled:cursor-not-allowed',
        sizes[size],
        variants[variant],
        className,
      )}
      {...props}
    />
  );
});
Button.displayName = 'Button';

export const TextInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & {
    label?: string;
    hint?: string;
    error?: string | null;
  }
>(({ label, hint, error, className, id, ...props }, ref) => {
  const inputId = id ?? props.name;
  return (
    <label className="block space-y-1.5" htmlFor={inputId}>
      {label && (
        <span className="text-small font-medium text-ink">{label}</span>
      )}
      <input
        ref={ref}
        id={inputId}
        className={cls(
          'w-full min-h-[44px] rounded-md border border-border bg-surface px-3 py-2.5 text-body text-ink placeholder:text-ink-muted transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30',
          error && 'border-neg focus:border-neg focus:ring-neg/30',
          className,
        )}
        {...props}
      />
      {hint && !error && (
        <span className="text-caption text-ink-muted">{hint}</span>
      )}
      {error && <span className="text-caption text-neg">{error}</span>}
    </label>
  );
});
TextInput.displayName = 'TextInput';

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; hint?: string }
>(({ label, hint, className, ...props }, ref) => (
  <label className="block space-y-1.5">
    {label && <span className="text-small font-medium text-ink">{label}</span>}
    <textarea
      ref={ref}
      className={cls(
        'w-full rounded-md border border-border bg-surface px-3 py-2.5 text-body text-ink placeholder:text-ink-muted transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30',
        className,
      )}
      {...props}
    />
    {hint && <span className="text-caption text-ink-muted">{hint}</span>}
  </label>
));
TextArea.displayName = 'TextArea';

export const Select: React.FC<{
  label?: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}> = ({ label, hint, value, onChange, options, placeholder }) => (
  <label className="block space-y-1.5">
    {label && <span className="text-small font-medium text-ink">{label}</span>}
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full min-h-[44px] rounded-md border border-border bg-surface px-3 py-2.5 text-body text-ink transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    {hint && <span className="text-caption text-ink-muted">{hint}</span>}
  </label>
);

export const Checkbox: React.FC<{
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, hint, checked, onChange }) => (
  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-body">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-border bg-surface text-brand focus:ring-brand/30"
    />
    <span>
      <span className="text-ink">{label}</span>
      {hint && <span className="block text-caption text-ink-muted">{hint}</span>}
    </span>
  </label>
);

export const Spinner: React.FC = () => (
  <span
    aria-hidden
    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-border border-t-brand"
  />
);

export const Pill: React.FC<{
  children: ReactNode;
  tone?: 'neutral' | 'green' | 'amber' | 'red' | 'indigo';
}> = ({ children, tone = 'neutral' }) => {
  const tones = {
    neutral: 'bg-surface-raised text-ink-muted',
    green:   'bg-emerald-50 text-emerald-700 border border-emerald-200',
    amber:   'bg-amber-50 text-amber-700 border border-amber-200',
    red:     'bg-red-50 text-neg border border-red-200',
    indigo:  'bg-brand-light text-brand border border-brand/20',
  };
  return (
    <span
      className={cls(
        'inline-flex items-center rounded-full px-2 py-0.5 text-caption font-medium',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
};

export const ErrorBanner: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="rounded-md border border-red-200 bg-red-50 p-3 text-body text-neg">
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------

export const EmptyState: React.FC<{
  icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}> = ({ icon: Icon, title, description, action, className }) => (
  <div
    className={cls(
      'flex flex-col items-center justify-center gap-3 rounded-card border border-dashed border-border bg-surface/60 px-6 py-12 text-center',
      className,
    )}
  >
    <Icon className="h-10 w-10 text-ink-muted opacity-50" strokeWidth={1.5} />
    <div>
      <div className="text-h2 text-ink">{title}</div>
      {description && (
        <div className="mt-1 max-w-sm text-small text-ink-muted">{description}</div>
      )}
    </div>
    {action && <div className="mt-2">{action}</div>}
  </div>
);

// ---------------------------------------------------------------------------
// OverflowMenu
// ---------------------------------------------------------------------------

export interface OverflowMenuItem {
  label: string;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
  onClick?: () => void;
  href?: string;
  download?: string;
  target?: string;
  danger?: boolean;
  disabled?: boolean;
  separator?: boolean;
}

export const OverflowMenu: React.FC<{
  items: OverflowMenuItem[];
  align?: 'left' | 'right';
  label?: string;
}> = ({ items, align = 'right', label = 'More actions' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-surface text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink"
      >
        <MoreHorizontal className="h-4 w-4" strokeWidth={2} />
      </button>
      {open && (
        <div
          role="menu"
          className={cls(
            'absolute top-full z-50 mt-1 min-w-[200px] overflow-hidden rounded-card border border-border bg-surface py-1 shadow-[0_4px_16px_rgba(0,0,0,0.10)]',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {items.map((it, i) => {
            if (it.separator) {
              return <div key={i} className="my-1 h-px bg-border" />;
            }
            const Icon = it.icon;
            const body = (
              <>
                {Icon && <Icon className="h-4 w-4" strokeWidth={2} />}
                <span className="flex-1 text-left">{it.label}</span>
              </>
            );
            const common = cls(
              'flex w-full min-h-[44px] items-center gap-2 px-3 py-2 text-small transition-colors',
              it.disabled
                ? 'cursor-not-allowed text-ink-muted'
                : it.danger
                ? 'text-neg hover:bg-red-50'
                : 'text-ink hover:bg-surface-raised',
            );
            if (it.href) {
              return (
                <a
                  key={i}
                  role="menuitem"
                  href={it.href}
                  download={it.download}
                  target={it.target}
                  rel={it.target === '_blank' ? 'noopener' : undefined}
                  className={common}
                  onClick={() => setOpen(false)}
                >
                  {body}
                </a>
              );
            }
            return (
              <button
                key={i}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                className={common}
                onClick={() => {
                  setOpen(false);
                  it.onClick?.();
                }}
              >
                {body}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
